import { Injectable } from "@angular/core"
import { AngularFirestore } from "@angular/fire/firestore"
import { DevicePlatforms, TrEditableDropdownOption, WebPlatforms } from "@tr/models"
import { getDataItem } from "@tr/utils"
import { DataService } from "./data.service"

@Injectable({ providedIn: "root" })
export class GlobalCollectionUtils {
  public testTypes = []
  public authors: string[] = []
  public companyEnvironments: any[] = []
  public environments: string[] = []
  public tags = []
  public features = []
  public webPlatforms = []
  public mobilePlatforms = []
  public platforms = []
  public devices = []
  public emulators = []

  constructor(
    private firestore: AngularFirestore,
    private dataService: DataService,
  ) {}

  public async loadPlatformsAndDevicesData(): Promise<void> {
    const webPlatforms = await getDataItem<WebPlatforms>(
      this.firestore
        .collection("platforms")
        .doc<WebPlatforms>("web"),
    )

    this.webPlatforms = []

    for (const webOsWindowsVersion of webPlatforms.webOsVersionsWindows) {
      for (const browser of webOsWindowsVersion.browsers) {
        const index = this.webPlatforms.findIndex(x => x === browser.name)
        if (index === -1) {
          this.webPlatforms.push(browser.name)
        }
      }
    }
    for (const webOsMacVersion of webPlatforms.webOsVersionsMac) {
      for (const browser of webOsMacVersion.browsers) {
        const index = this.webPlatforms.findIndex(x => x === browser.name)
        if (index === -1) {
          this.webPlatforms.push(browser.name)
        }
      }
    }
    for (const webOsUbuntuVersion of webPlatforms.webOsVersionsUbuntu) {
      for (const browser of webOsUbuntuVersion.browsers) {
        const index = this.webPlatforms.findIndex(x => x === browser.name)
        if (index === -1) {
          this.webPlatforms.push(browser.name)
        }
      }
    }

    this.webPlatforms.forEach((_, i) => {
      if (this.webPlatforms[i] === "Edge") {
        this.webPlatforms[i] = "Microsoft Edge"
      }
    })

    this.mobilePlatforms = webPlatforms.mobileBrowserOsVersions

    this.platforms = [
      ...this.webPlatforms.map(web => ({ label: web, value: web, group: 'Web' } as TrEditableDropdownOption)),
      ...this.mobilePlatforms.map(mobile => ({ label: mobile.name, value: mobile.name, group: 'Mobile' } as TrEditableDropdownOption)),
    ]

    const platformDevices = await getDataItem<DevicePlatforms>(
      this.firestore
        .collection("platforms")
        .doc("devices"),
    )

    this.devices = platformDevices.devices
    this.emulators = platformDevices.emulators
  }

  public async loadFeaturesData(company: string, project: string): Promise<void> {
    let tempFeature = [] as any
    const featuresRef = await this.firestore
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection("Features")
      .ref.get()
    const featureData = featuresRef.docs.map(feature => feature.data())
    if (featureData) {
      for (let i = 0; i < featureData.length; i++) {
        tempFeature.push(featureData[i].featureName)
        if (i === 0) {
          this.features = tempFeature.slice()
        } else {
          this.features = this.features.concat(tempFeature)
        }
        tempFeature = []
      }
    }
  }

  public async loadTagsData(company: string, project: string): Promise<void> {
    let tempTag = [] as any
    const tagsRef = await this.firestore
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection("Tags")
      .ref.get()
    const tagsData = tagsRef.docs.map(tag => tag.data())
    if (tagsData) {
      for (let i = 0; i < tagsData.length; i++) {
        tempTag.push(tagsData[i].tagName)
        if (i === 0) {
          this.tags = tempTag.slice()
        } else {
          this.tags = this.tags.concat(tempTag)
        }
        tempTag = []
      }
    }
  }

  public async loadEnvironmentData(company: string, project: string): Promise<void> {
    let tempEnvironment = [] as any
    const environmentRef = await this.firestore
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection("environment")
      .ref.get()
    const environmentData = environmentRef.docs.map(env => env.data())
    if (environmentData) {
      for (let i = 0; i < environmentData.length; i++) {
        tempEnvironment.push(environmentData[i].name)
        if (i === 0) {
          this.environments = tempEnvironment.slice()
        } else {
          this.environments = this.environments.concat(tempEnvironment)
        }
        tempEnvironment = []
      }
    }
  }

  public async loadTestTypes(): Promise<void> {
    let tempTypes = [] as any
    const testTypesRef = await this.firestore
      .collection("testTypes")
      .ref.get()
    const testTypesData = testTypesRef.docs.map(testType => testType.data())
    if (testTypesData) {
      if (testTypesData.length !== 0) {
        const index = testTypesData.findIndex(x => x.name === "types")
        tempTypes.push("<No Selection>")
        for (let i = 0; i < testTypesData[index].types.length; i++) {
          tempTypes.push(testTypesData[index].types[i])
          if (i === 0) {
            this.testTypes = tempTypes.slice()
          } else {
            this.testTypes = this.testTypes.concat(tempTypes)
          }
          tempTypes = []
        }
      }
    }
  }

  public async loadAuthors(company: string): Promise<void> {
    const usersSnapshot = await this.firestore
      .collection("companies")
      .doc(company)
      .collection("users")
      .ref.get()

    this.authors = usersSnapshot.docs.map(doc => {
      const user = doc.data()
      return user.firstName + " " + user.lastName
    }).sort()
  }
}
